// Pair of (value, suffix) strings for rendering a SOW currency surface.
// Splitting the suffix out lets the consumer apply a quieter slate-secondary
// tone to it so the basis label is visible but does not compete with the
// dollar figure. Reuses `gstSuffix` so the basis text stays in sync with the
// rest of the contract surfaces (milestone summary, contract detail, etc.).
//
// SOW values and variation impacts are stored with up to 2 decimal places
// (cents precision) so contract documents and totals reconcile exactly.
// `precision='cents'` (the default) renders all 2 decimal places; summary
// tiles and abbreviated legends opt into `precision='whole'` for visual
// economy where exact cents would crowd the surface.

import { gstSuffix } from "../../../../utils/gstSuffix";

export type SowCurrencyPrecision = "cents" | "whole";

export interface FormattedSowCurrency {
  /** Formatted dollar figure with thousands separators and `$` prefix.
   *  At 'cents' precision the value carries 2 fractional digits; at
   *  'whole' precision it is rounded to the nearest integer dollar. */
  value: string;
  /** Bracketed GST hint: "ex-GST" / "no GST" / "GST status not recorded".
   *  Empty string when `gstApplies` is omitted on a surface that should
   *  hide the basis (no current surface does — kept for symmetry). */
  suffix: string;
}

export function formatSowCurrency(
  amount: number,
  gstApplies: boolean | undefined,
  precision: SowCurrencyPrecision = "cents",
): FormattedSowCurrency {
  const formatted =
    precision === "whole"
      ? Math.round(amount).toLocaleString("en-AU")
      : amount.toLocaleString("en-AU", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
  return { value: `$${formatted}`, suffix: gstSuffix(gstApplies) };
}

/** Compact abbreviated form for stacked-bar legends (`$1.2M`, `$200k`).
 *  Brand SKILL.md §9.5 calls for abbreviated currency in the legend so the
 *  primary value above stays the precise figure. Cents in the input are
 *  rounded — at this scale they're below the visible precision. */
export function formatSowCurrencyAbbreviated(amount: number): string {
  const abs = Math.abs(amount);
  const sign = amount < 0 ? "-" : "";
  if (abs >= 1_000_000) {
    return `${sign}$${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  }
  if (abs >= 1_000) {
    return `${sign}$${Math.round(abs / 1_000)}k`;
  }
  return `${sign}$${Math.round(abs)}`;
}
